import type { Level, Medium } from '../../../src/contracts/types.ts';

/**
 * 端末内に保存する利用者設定。
 *
 * 会員機能（WebMado 連携）が入るまでは localStorage のみを使う。
 * 会員機能の導入後は、この Preferences をサーバ側の設定と同期させる。
 */

export interface Preferences {
  /** 初回設定を完了したか。 */
  onboarded: boolean;
  /** 興味のある分野コード。空なら全分野。 */
  categories: string[];
  /** 既定の理解レベル。 */
  level: Level;
  /** 既定のメディア。 */
  medium: Medium;
  theme: 'light' | 'dark';
  /** 引用グラフの既定の向き。 */
  graphDirection: 'all' | 'references' | 'cited_by';
}

export interface HistoryEntry {
  paperId: string;
  slug: string;
  title: string;
  headline: string;
  categoryName: string;
  viewedAt: string;
}

const PREF_KEY = 'paperstock.preferences';
const BOOKMARK_KEY = 'paperstock.bookmarks';
const HISTORY_KEY = 'paperstock.history';
const THEME_KEY = 'paperstock.theme';
const MAX_HISTORY = 200;

const DEFAULT_PREFERENCES: Preferences = {
  onboarded: false,
  categories: [],
  level: 'beginner',
  medium: 'text',
  theme: 'light',
  graphDirection: 'all',
};

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return { ...fallback, ...(JSON.parse(raw) as object) } as T;
  } catch {
    return fallback;
  }
}

function readArray<T>(key: string): T[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const value = JSON.parse(raw);
    return Array.isArray(value) ? (value as T[]) : [];
  } catch {
    return [];
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // プライベートブラウズなどで保存できない場合も、閲覧自体は続けられるようにする。
  }
}

export function getPreferences(): Preferences {
  const stored = read<Preferences>(PREF_KEY, DEFAULT_PREFERENCES);
  return {
    ...stored,
    level: 'beginner',
    medium: 'text',
    // 旧既定の system も白へ移行する。暗色は利用者が明示した場合だけ使う。
    theme: stored.theme === 'dark' ? 'dark' : 'light',
  };
}

export function savePreferences(patch: Partial<Preferences>): Preferences {
  const next = { ...getPreferences(), ...patch, level: 'beginner' as const, medium: 'text' as const };
  write(PREF_KEY, next);
  if (patch.theme) applyTheme(patch.theme);
  return next;
}

export function applyTheme(theme: Preferences['theme']): void {
  document.documentElement.setAttribute('data-theme', theme);
  document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')?.setAttribute(
    'content',
    theme === 'dark' ? '#090d14' : '#ffffff',
  );
  try {
    localStorage.setItem(THEME_KEY, theme);
  } catch {
    /* 同上 */
  }
}

export function cycleTheme(): Preferences['theme'] {
  const order: Array<Preferences['theme']> = ['light', 'dark'];
  const current = getPreferences().theme;
  const next = order[(order.indexOf(current) + 1) % order.length] ?? 'light';
  savePreferences({ theme: next });
  return next;
}

// --- ブックマーク -----------------------------------------------------------

export interface Bookmark {
  paperId: string;
  slug: string;
  title: string;
  headline: string;
  categoryName: string;
  addedAt: string;
  /**
   * 外した時刻（墓標）。**行そのものは消さない。**
   *
   * 消すと、2つの端末を同期したときに片方で外したブックマークが
   * もう片方から復活する（「無い」と「消した」を区別できないため）。
   */
  deletedAt?: string;
}

/** 画面に出すぶん。外したものは含めない。 */
export function getBookmarks(): Bookmark[] {
  return readArray<Bookmark>(BOOKMARK_KEY).filter((b) => b.deletedAt === undefined);
}

/** 同期に送るぶん。墓標も含める。 */
export function getBookmarksForSync(): Bookmark[] {
  return readArray<Bookmark>(BOOKMARK_KEY);
}

/** 同期の結果を書き戻す。墓標ごと保存する。 */
export function replaceBookmarks(entries: Bookmark[]): void {
  write(BOOKMARK_KEY, entries);
}

export function isBookmarked(paperId: string): boolean {
  return getBookmarks().some((b) => b.paperId === paperId);
}

export function toggleBookmark(entry: Omit<Bookmark, 'addedAt'>): boolean {
  const current = getBookmarksForSync();
  const now = new Date().toISOString();
  const existing = current.findIndex((b) => b.paperId === entry.paperId);
  if (existing >= 0) {
    const found = current[existing] as Bookmark;
    if (found.deletedAt === undefined) {
      // 外す。行は残し、墓標を立てる。
      current[existing] = { ...found, deletedAt: now };
      write(BOOKMARK_KEY, current);
      return false;
    }
    // 付け直す。墓標を外す。
    const restored: Bookmark = { ...found, ...entry, addedAt: now };
    delete restored.deletedAt;
    current[existing] = restored;
    write(BOOKMARK_KEY, current);
    return true;
  }
  current.unshift({ ...entry, addedAt: now });
  write(BOOKMARK_KEY, current);
  return true;
}

// --- 閲覧履歴 --------------------------------------------------------------

export function getHistory(): HistoryEntry[] {
  return readArray<HistoryEntry>(HISTORY_KEY);
}

export function recordHistory(entry: Omit<HistoryEntry, 'viewedAt'>): void {
  const current = getHistory().filter((h) => h.paperId !== entry.paperId);
  current.unshift({ ...entry, viewedAt: new Date().toISOString() });
  write(HISTORY_KEY, current.slice(0, MAX_HISTORY));
}

export function clearHistory(): void {
  write(HISTORY_KEY, []);
}

/** 同期の結果を書き戻す。 */
export function replaceHistory(entries: HistoryEntry[]): void {
  write(HISTORY_KEY, entries.slice(0, MAX_HISTORY));
}
